import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import _ from "underscore";
import { useIntersection } from "../../Hooks/customHooks";
import { START_GET_DRIVER_LIST } from "../../Actions/SurgeryAction";
import {
  Checkbox,
  Box,
  Chip,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Autocomplete from "@mui/material/Autocomplete";
import "../../Sass/Common.scss";
import { getEllipsisName } from "../../utils/generalUtils";
import HtmlTooltipMain from "../../Components/Common/HtmlTooltipMain";

const useStyles = makeStyles((theme) => ({
  autocompleteMain: {
    background: theme.palette.white.main,
    boxShadow: "0px 0px 10px rgb(90 104 114 / 10%)",
    borderRadius: "4px",
    marginRight: "20px !important",
    minWidth: "250px !important",
    "& .MuiOutlinedInput-notchedOutline": {
      border: 0,
    },
  },
}));

function DriverAutoComplete(props) {
  const {
    value,
    multiple,
    disabled,
    handleChange,
    driverList,
    getDriverAction,
    apiParams,
    disableEnter = true,
    maxValue,
    driverListFlag,
    maxVisibleChip = 1,
    textFieldProps = {
      required: true,
    },
    ...other
  } = props;
  const classes = useStyles();
  const [selectDriver, setSelectDriver] = useState(value || {});
  const [isOpen, setIsOpen] = useState(false);
  const [setTargetDriver, entryDriver] = useIntersection({ threshold: 0.1 });
  const surgeryListRecordLimit = 20;

  /* Value Set */
  useEffect(() => {
    if (multiple) {
      setSelectDriver(Object.values(value || {}));
    } else {
      setSelectDriver(value);
    }
  }, [value]);

  /* Show data based on scroll */
  useEffect(() => {
    if (entryDriver.isIntersecting && driverList.loading === false) {
      let params = {
        offset: driverList?.data?.list?.length || 0,
        ...apiParams,
      };
      getDriverAction(params);
    }
  }, [entryDriver]);

  //* Fetch Driver List */
  let fetchDriver = () => {
    setIsOpen(true);
    let params = {
      offset: 0,
      ...apiParams,
    };
    getDriverAction(params);
  };

  // /* Driver List Loading useEffect */
  // useEffect(() => {
  //   if (!driverList?.loading && !Object.keys(driverList?.data)?.length) {
  //     getDriverAction({ offset: 0, limit: surgeryListRecordLimit });
  //   }
  // }, [driverList]);

  /* Handle Change Driver List */
  const handleChangeDriverList = (event, newValue) => {
    let newList = {};
    if (multiple) {
      newValue.map((driver) => {
        newList[driver.id] = driver;
      });
    } else {
      newList = newValue;
    }
    setSelectDriver(newValue);
    handleChange && handleChange(newList);
  };

  /** search surgery  */
  const searchSurgeryDebounce = useRef(
    _.debounce((searchString, open) => {
      let params = {
        offset: 0,
      };
      if (searchString) {
        params.q = searchString;
      }
      if (open) {
        getDriverAction(params);
      }
    }, 500)
  );

  /** handle driver search */
  const handleDriverSearch = (newValue) => {
    searchSurgeryDebounce.current(newValue, isOpen);
  };

  return (
    <>
      <Autocomplete
        multiple={multiple}
        classes={{
          option: "option",
          noOptions: "noOptions",
        }}
        disableCloseOnSelect={multiple}
        disabled={Boolean(disabled)}
        onOpen={fetchDriver}
        onClose={() => {
          setIsOpen(false);
        }}
        className={classes.autocompleteMain}
        noOptionsText={"No results found"}
        value={selectDriver}
        onChange={handleChangeDriverList}
        options={driverList?.data?.list || []}
        getOptionLabel={(option) => option.name || ""}
        isOptionEqualToValue={(option, value) => {
          return option.id === value.id;
        }}
        onInputChange={(event, newValue) => {
          handleDriverSearch(newValue);
        }}
        limitTags={1}
        filterOptions={(option) => {
          let result = [...option];
          if (driverList?.data?.list?.length < driverList?.data?.total) {
            result.push("FinalOption");
          }

          if (driverList?.data?.list?.length === 0) {
            result.push({
              name: `No option found`,
            });
          }
          return result || [];
        }}
        renderOption={(props, option, { selected }) => {
          return (
            <React.Fragment>
              {typeof option === "object" ? (
                <Grid {...props} key={option?.id}>
                  {multiple && option.id && <Checkbox checked={selected} />}
                  {option.name}
                </Grid>
              ) : (
                <Grid className="autoCompleteInputLoader" ref={setTargetDriver}>
                  <CircularProgress size={20} />
                </Grid>
              )}
            </React.Fragment>
          );
        }}
        getOptionDisabled={(option) => {
          if (option.name === "No option found") {
            return true;
          }
        }}
        renderTags={(tagValue, getTagProps) => {
          if (tagValue.length === maxVisibleChip) {
            return tagValue.map((option, index) => {
              return (
                <HtmlTooltipMain
                  className={classes.rsHtmltoolTip}
                  title={option?.name}
                >
                  <Chip
                    key={index}
                    label={`${option.name}`}
                    {...getTagProps({ index })}
                    size="small"
                  />
                </HtmlTooltipMain>
              );
            });
          } else {
            return (
              <Box display="flex" alignItems="center">
                {tagValue
                  .slice(0, driverListFlag === true ? maxValue : maxVisibleChip)
                  .map((option, index) => (
                    <HtmlTooltipMain
                      className={classes.rsHtmltoolTip}
                      title={tagValue[index].name}
                    >
                      <Chip
                        label={`${getEllipsisName(tagValue[index].name, 8)}`}
                        {...getTagProps({ index: index })}
                        size="small"
                      />
                    </HtmlTooltipMain>
                  ))}
                {tagValue.length > maxVisibleChip && (
                  <HtmlTooltipMain
                    interactive={true}
                    title={
                      <Grid>
                        {tagValue.slice(maxVisibleChip).map((option, index) => (
                          <Box>{option.name}</Box>
                        ))}
                      </Grid>
                    }
                  >
                    <Box>
                      +{" "}
                      {tagValue.length -
                        Math.min(maxVisibleChip, tagValue.length)}{" "}
                      more
                    </Box>
                  </HtmlTooltipMain>
                )}
              </Box>
            );
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            InputLabelProps={{ required: false }}
            inputProps={{
              ...params.inputProps,
            }}
          />
        )}
        {...other}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  driverList: state.surgery.driverList,
});

const mapDispatchToProps = (dispatch) => ({
  getDriverAction: (payload) =>
    dispatch({ type: START_GET_DRIVER_LIST, payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(DriverAutoComplete);
